import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoDirective } from '@jsverse/transloco';
import { Button } from 'primeng/button';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

import { BrandingService, ConsentService } from '../../../core/services';

/**
 * RODO/GDPR cookie-consent banner for white-label hosts.
 *
 * Shown ONLY when the host actually configured a COOKIE-based analytics tag
 * (`branding.hasCookieAnalytics()` — GA4 / Meta Pixel / GTM) AND the visitor
 * has not yet decided. A host running only cookieless CapyStats needs no
 * banner, so none is shown there.
 * Offers: "Akceptuj wszystko" / "Tylko niezbędne" / "Ustawienia" (expands
 * per-category toggles for Analityka and Marketing). Necessary/functional
 * cookies are always-on and not toggleable.
 *
 * Mounted globally (in App) so it appears on the white-label property/portfolio
 * page, which renders independently of the main-layout chrome.
 */
@Component({
  selector: 'app-consent-banner',
  standalone: true,
  imports: [FormsModule, TranslocoDirective, Button, ToggleSwitchModule],
  templateUrl: './consent-banner.component.html',
  styleUrl: './consent-banner.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConsentBannerComponent {
  private readonly branding = inject(BrandingService);
  private readonly consent = inject(ConsentService);

  /**
   * The banner is only relevant on branded hosts that run cookie-based tags
   * (GA4 / Meta Pixel / GTM) and where the visitor has not yet decided.
   * Cookieless-only (CapyStats) hosts never show it.
   */
  readonly visible = computed(
    () =>
      this.branding.isWhiteLabel() &&
      this.branding.hasCookieAnalytics() &&
      !this.consent.decided(),
  );

  readonly displayName = this.branding.displayName;

  /** Whether the expandable category settings are shown. */
  readonly showSettings = signal(false);

  /** Local, editable selection for the settings panel. */
  readonly analyticsSelected = signal(this.consent.analyticsGranted());
  readonly marketingSelected = signal(this.consent.marketingGranted());

  acceptAll(): void {
    this.consent.acceptAll();
  }

  rejectNonEssential(): void {
    this.consent.rejectNonEssential();
  }

  toggleSettings(): void {
    // Seed toggles from the currently persisted choice when opening.
    if (!this.showSettings()) {
      this.analyticsSelected.set(this.consent.analyticsGranted());
      this.marketingSelected.set(this.consent.marketingGranted());
    }
    this.showSettings.update((v) => !v);
  }

  savePreferences(): void {
    this.consent.save({
      analytics: this.analyticsSelected(),
      marketing: this.marketingSelected(),
    });
  }
}
